import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiClient, responseData } from "@/api/client";
import type { CardSettlement, MatchGroup, MoneyRow, PageResponse, ReconciliationRun } from "@/api/types";
import { useAuth } from "@/app/auth";
import { useWorkspace } from "@/app/workspace";
import { useDomainWorkflow } from "@/components/DomainShell";
import type { ImportWorkspaceConfig } from "@/components/ImportWorkspace";
import { MatchSpine } from "@/components/MatchSpine";
import { Empty, Panel } from "@/components/Page";
import { Pagination } from "@/components/Pagination";
import { useToast } from "@/components/Toast";
import { money, shortDate } from "@/components/format";
import { Provenance } from "@/components/WorkflowViews";

type CardWorkspaceView = "data" | "reconciliation";
type CardTab = "auto" | "criteria" | "pending";
const cardWorkspaceTab = (view: CardWorkspaceView): CardTab => view === "data" ? "pending" : "auto";
type CardManualSelectionStatus = "EMPTY" | "CONFIGURATION_MISSING" | "CURRENCY_MISMATCH" | "AMOUNT_MISMATCH" | "DIRECTION_MISMATCH" | "READY";
type CardSettlementSelection = Map<string, CardSettlement>;
type CardLedgerSelection = Map<string, MoneyRow>;

type CardManualTotals = {
  count: number;
  total: bigint;
  debit: bigint;
  credit: bigint;
  unclassified: bigint;
  signed: bigint;
  currencies: string[];
};

type CardManualValidation = {
  status: CardManualSelectionStatus;
  valid: boolean;
  difference: bigint;
  title: string;
  message: string;
  currency?: string;
};

const cardImportConfig: ImportWorkspaceConfig = { domain: "CARD", accountKind: "CARD", apiBase: "/cards", basePath: "/tarjetas", noun: "tarjetas" };
const absCents = (value: bigint) => value < 0n ? -value : value;
const valueToCents = (value: string | number | undefined): bigint => {
  const normalized = String(value ?? 0).trim().replace(",", ".");
  const match = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(normalized);
  if (!match) return 0n;
  const fraction = match[3] ?? "";
  const padded = fraction.padEnd(3, "0");
  let cents = BigInt(match[2]) * 100n + BigInt(padded.slice(0, 2));
  if (Number(padded[2]) >= 5) cents += 1n;
  return match[1] === "-" ? -cents : cents;
};
const centsToDecimal = (value: bigint): string => {
  const absolute = absCents(value);
  const sign = value < 0n ? "-" : "";
  return `${sign}${absolute / 100n}.${String(absolute % 100n).padStart(2, "0")}`;
};
const centsMoney = (value: bigint, currency: string) => money(centsToDecimal(value), currency);
const selectedCurrencies = <TRow extends { currency: string }>(selection: ReadonlyMap<string, TRow>) => [...new Set([...selection.values()].map((row) => row.currency))];
const settlementTotals = (selection: ReadonlyMap<string, CardSettlement>): CardManualTotals => {
  let total = 0n;
  let debit = 0n;
  let credit = 0n;
  let unclassified = 0n;
  selection.forEach((row) => {
    const gross = absCents(valueToCents(row.grossAmount));
    total += gross;
    if (row.cardFunding === "DEBIT") debit += gross;
    else if (row.cardFunding === "CREDIT") credit += gross;
    else unclassified += gross;
  });
  return { count: selection.size, total, debit, credit, unclassified, signed: total, currencies: selectedCurrencies(selection) };
};
const ledgerTotals = (selection: ReadonlyMap<string, MoneyRow>): CardManualTotals => {
  let debit = 0n;
  let credit = 0n;
  selection.forEach((row) => {
    debit += absCents(valueToCents(row.debit));
    credit += absCents(valueToCents(row.credit));
  });
  const signed = debit - credit;
  return { count: selection.size, total: absCents(signed), debit, credit, unclassified: 0n, signed, currencies: selectedCurrencies(selection) };
};
const validateCardManualSelection = (origin: CardManualTotals, counterpart: CardManualTotals, maxDifference: bigint | null): CardManualValidation => {
  const currencies = [...new Set([...origin.currencies, ...counterpart.currencies])];
  const difference = absCents(origin.total - counterpart.signed);
  if (!origin.count || !counterpart.count) {
    const message = !origin.count && !counterpart.count
      ? "Seleccioná al menos una liquidación y un asiento del Libro Mayor."
      : !origin.count ? "Seleccioná al menos una liquidación." : "Seleccioná al menos un asiento del Libro Mayor.";
    return { status: "EMPTY", valid: false, difference, title: "Armá la selección", message };
  }
  if (currencies.length !== 1) {
    return { status: "CURRENCY_MISMATCH", valid: false, difference, title: "Monedas distintas", message: "La selección debe usar una sola moneda en ambos lados." };
  }
  if (maxDifference === null) {
    return { status: "CONFIGURATION_MISSING", valid: false, difference, title: "Configuración no disponible", message: "No se pudo obtener el tope de diferencia permitido. Recargá la página antes de conciliar.", currency: currencies[0] };
  }
  if (counterpart.signed <= 0n) {
    return { status: "DIRECTION_MISMATCH", valid: false, difference, title: "Sentido incompatible", message: "El neto del Libro Mayor debe quedar en el Debe para representar las ventas liquidadas.", currency: currencies[0] };
  }
  if (difference > maxDifference) {
    const missingSide = origin.total < counterpart.signed ? "Liquidaciones" : "Libro Mayor";
    return {
      status: "AMOUNT_MISMATCH",
      valid: false,
      difference,
      title: "La diferencia supera el tope",
      message: `Liquidaciones ${centsMoney(origin.total, currencies[0])} · Libro Mayor ${centsMoney(counterpart.signed, currencies[0])} · Diferencia ${centsMoney(difference, currencies[0])}. Supera el tope permitido de ${centsMoney(maxDifference, currencies[0])}; faltan ${centsMoney(difference, currencies[0])} en ${missingSide}.`,
      currency: currencies[0],
    };
  }
  return {
    status: "READY",
    valid: true,
    difference,
    title: difference === 0n ? "Selección balanceada" : "Diferencia dentro del tope",
    message: difference === 0n
      ? `Los importes coinciden. Tope permitido: ${centsMoney(maxDifference, currencies[0])}.`
      : `La diferencia de ${centsMoney(difference, currencies[0])} está dentro del tope permitido de ${centsMoney(maxDifference, currencies[0])}.`,
    currency: currencies[0],
  };
};
const toggleSelectedRow = <TRow extends { id: string }>(selection: Map<string, TRow>, row: TRow) => {
  const next = new Map(selection);
  if (next.has(row.id)) next.delete(row.id);
  else next.set(row.id, row);
  return next;
};
const toggleSelectedPage = <TRow extends { id: string }>(selection: Map<string, TRow>, rows: TRow[]) => {
  const next = new Map(selection);
  const allSelected = rows.length > 0 && rows.every((row) => next.has(row.id));
  rows.forEach((row) => { if (allSelected) next.delete(row.id); else next.set(row.id, row); });
  return next;
};
const rowAmount = (row: MoneyRow) => Number(row.debit ?? 0) !== 0 ? Number(row.debit) : Number(row.credit ?? 0);
const selectionMoney = (value: bigint, currencies: string[]) => currencies.length === 1 ? centsMoney(value, currencies[0]) : currencies.length > 1 ? "Monedas mixtas" : centsMoney(value, "ARS");
const BalancedCheckIcon = ({ className }: { className?: string }) => <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m5 12 4 4L19 6" /></svg>;

function CardManualProofSide({ label, totals, kind }: { label: string; totals: CardManualTotals; kind: "SETTLEMENT" | "LEDGER" }) {
  return <div className="manual-selection-proof__side">
    <div className="manual-selection-proof__heading"><span>{label}</span><b>{totals.count} {totals.count === 1 ? "seleccionado" : "seleccionados"}</b></div>
    <strong className="manual-selection-proof__total mono">{selectionMoney(totals.total, totals.currencies)}</strong>
    <div className="manual-selection-proof__breakdown">
      {kind === "SETTLEMENT" ? <>
        <span>Débito <b className="mono">{selectionMoney(totals.debit, totals.currencies)}</b></span>
        <span>Crédito <b className="mono">{selectionMoney(totals.credit, totals.currencies)}</b></span>
        {totals.unclassified > 0n && <span>Sin clasificar <b className="mono">{selectionMoney(totals.unclassified, totals.currencies)}</b></span>}
      </> : <>
        <span>Debe <b className="mono">{selectionMoney(totals.debit, totals.currencies)}</b></span>
        <span>Haber <b className="mono">{selectionMoney(totals.credit, totals.currencies)}</b></span>
      </>}
    </div>
  </div>;
}

function CardSelectedRows({ title, rows, disabled, onRemove, onClear }: { title: string; rows: Array<CardSettlement | MoneyRow>; disabled: boolean; onRemove(row: CardSettlement | MoneyRow): void; onClear(): void }) {
  const settlements = title === "Liquidaciones";
  return <section className="manual-selection-detail__column" aria-label={`Selección de ${title}`}>
    <header><div><small>{title}</small><strong>{rows.length} {rows.length === 1 ? "registro" : "registros"}</strong></div><button type="button" className="button button--small" disabled={!rows.length || disabled} onClick={onClear}>Limpiar {title}</button></header>
    {rows.length ? <div className="manual-selection-detail__list">{rows.map((row) => {
      const settlement = row as CardSettlement;
      const ledger = row as MoneyRow;
      const description = settlements
        ? settlement.couponNumber ? `Cupón ${settlement.couponNumber}` : settlement.settlementNumber ? `Liquidación ${settlement.settlementNumber}` : "Liquidación sin referencia"
        : ledger.description || ledger.reference || "Asiento sin descripción";
      const date = settlements ? settlement.operationDate ?? settlement.paymentDate ?? "" : ledger.transactionDate ?? "";
      const direction = settlements ? settlement.cardFunding === "DEBIT" ? "Débito" : settlement.cardFunding === "CREDIT" ? "Crédito" : "Sin clasificar" : Number(ledger.debit ?? 0) !== 0 ? "Debe" : "Haber";
      const amountValue = settlements ? Number(settlement.grossAmount ?? 0) : rowAmount(ledger);
      return <article className="manual-selection-detail__row" key={row.id}>
        <div><strong>{description}</strong><span><time className="mono" dateTime={date}>{shortDate(date)}</time> · {direction}</span></div>
        <b className="mono">{money(amountValue, row.currency)}</b>
        <button type="button" disabled={disabled} onClick={() => onRemove(row)} aria-label={`Quitar ${description} de ${title}`}>Quitar</button>
      </article>;
    })}</div> : <p className="manual-selection-detail__empty">Todavía no seleccionaste registros de este lado.</p>}
  </section>;
}

function CardManualSelectionBalance({ settlements, ledgerEntries, settlementSummary, ledgerSummary, validation, disabled, onRemoveSettlement, onRemoveLedger, onClearSettlements, onClearLedger, onClearAll }: {
  settlements: ReadonlyMap<string, CardSettlement>;
  ledgerEntries: ReadonlyMap<string, MoneyRow>;
  settlementSummary: CardManualTotals;
  ledgerSummary: CardManualTotals;
  validation: CardManualValidation;
  disabled: boolean;
  onRemoveSettlement(row: CardSettlement): void;
  onRemoveLedger(row: MoneyRow): void;
  onClearSettlements(): void;
  onClearLedger(): void;
  onClearAll(): void;
}) {
  const totalSelected = settlements.size + ledgerEntries.size;
  const proofValue = validation.status === "AMOUNT_MISMATCH" && validation.currency
    ? centsMoney(validation.difference, validation.currency)
    : validation.status === "CURRENCY_MISMATCH" ? "Monedas mixtas"
      : validation.status === "DIRECTION_MISMATCH" ? "Revisar Debe / Haber" : "—";
  return <section className={`manual-selection-balance manual-selection-balance--${validation.status.toLowerCase()}`} aria-label="Balance de conciliación manual">
    <div className="manual-selection-proof">
      <CardManualProofSide label="Liquidaciones" totals={settlementSummary} kind="SETTLEMENT" />
      <div className="manual-selection-proof__control" role="status" aria-live="polite" aria-atomic="true">
        <small>{validation.title}</small>
        {validation.status === "READY" ? <strong className="manual-selection-proof__check"><BalancedCheckIcon /></strong> : <strong className="mono">{proofValue}</strong>}
        <p>{validation.message}</p>
      </div>
      <CardManualProofSide label="Libro Mayor" totals={ledgerSummary} kind="LEDGER" />
    </div>
    {totalSelected > 0 && <details className="manual-selection-detail">
      <summary><span>Revisar selección</span><b>{totalSelected} {totalSelected === 1 ? "registro" : "registros"}</b></summary>
      <div className="manual-selection-detail__content">
        <CardSelectedRows title="Liquidaciones" rows={[...settlements.values()]} disabled={disabled} onRemove={(row) => onRemoveSettlement(row as CardSettlement)} onClear={onClearSettlements} />
        <CardSelectedRows title="Libro Mayor" rows={[...ledgerEntries.values()]} disabled={disabled} onRemove={(row) => onRemoveLedger(row as MoneyRow)} onClear={onClearLedger} />
      </div>
      <footer><button type="button" className="button button--small" disabled={disabled} onClick={onClearAll}>Limpiar toda la selección</button></footer>
    </details>}
  </section>;
}

function CardSettlementRows({ page, selected, disabled, onToggle, onTogglePage, onPage }: { page?: PageResponse<CardSettlement>; selected: ReadonlyMap<string, CardSettlement>; disabled: boolean; onToggle(row: CardSettlement): void; onTogglePage(rows: CardSettlement[]): void; onPage(page: number): void }) {
  const selectPageRef = useRef<HTMLInputElement>(null);
  const rows = page?.items ?? [];
  const selectedVisible = rows.filter((row) => selected.has(row.id)).length;
  const allSelected = rows.length > 0 && selectedVisible === rows.length;
  useEffect(() => { if (selectPageRef.current) selectPageRef.current.indeterminate = selectedVisible > 0 && !allSelected; }, [allSelected, selectedVisible]);
  const summary = <span className="ledger-panel-summary"><b>{page?.meta.total ?? 0} liquidaciones</b><span className="ledger-panel-summary__currency">Bruto {money(page?.totals?.debit ?? 0, rows[0]?.currency ?? "ARS")}</span></span>;
  return <Panel title="Liquidaciones pendientes" meta={summary} mobileCollapsible>
    <div className="transaction-selection-toolbar"><label className="check-field"><input ref={selectPageRef} type="checkbox" checked={allSelected} disabled={!rows.length || disabled} onChange={() => onTogglePage(rows)} aria-label="Seleccionar esta página de Liquidaciones pendientes"/><span>Seleccionar esta página</span></label><span>{selectedVisible} de {rows.length} en esta página{selected.size > selectedVisible ? ` · ${selected.size} en total` : ""}</span></div>
    <div className="transaction-list">{rows.map((row) => {
      const title = row.couponNumber ? `Cupón ${row.couponNumber}` : row.settlementNumber ? `Liquidación ${row.settlementNumber}` : "Liquidación sin referencia";
      const operationDate = row.operationDate ?? row.paymentDate ?? "";
      const funding = row.cardFunding === "DEBIT" ? "Débito" : row.cardFunding === "CREDIT" ? "Crédito" : "Sin clasificar";
      return <article className={`transaction-row${selected.has(row.id) ? " transaction-row--selected" : ""}`} key={row.id}>
        <div className="transaction-row__select"><input className="row-check" type="checkbox" checked={selected.has(row.id)} disabled={disabled} onChange={() => onToggle(row)} aria-label={`Seleccionar ${title}`}/></div>
        <div className="transaction-row__main"><strong>{title}</strong><div className="transaction-row__meta"><time className="mono" dateTime={operationDate}>{shortDate(operationDate)}</time>{row.settlementNumber && row.settlementNumber !== row.couponNumber && <span>Liquidación {row.settlementNumber}</span>}<span>{funding}</span></div><Provenance value={row.importBatch}/></div>
        <div className="transaction-row__amount"><span>Bruto</span><b className="mono">{money(row.grossAmount ?? 0, row.currency)}</b></div>
        <div className="transaction-row__controls"><span className="status status--warning">Pendiente</span>{row.periodRelation === "PRIOR" && <span className="status status--transit">Período anterior</span>}</div>
      </article>;
    })}</div>
    {!rows.length && <Empty>No hay liquidaciones para estos filtros.</Empty>}
    <Pagination meta={page?.meta} onPageChange={onPage}/>
  </Panel>;
}

function CardLedgerRows({ page, selected, disabled, onToggle, onTogglePage, onPage }: { page?: PageResponse<MoneyRow>; selected: ReadonlyMap<string, MoneyRow>; disabled: boolean; onToggle(row: MoneyRow): void; onTogglePage(rows: MoneyRow[]): void; onPage(page: number): void }) {
  const selectPageRef = useRef<HTMLInputElement>(null);
  const rows = page?.items ?? [];
  const selectedVisible = rows.filter((row) => selected.has(row.id)).length;
  const allSelected = rows.length > 0 && selectedVisible === rows.length;
  useEffect(() => { if (selectPageRef.current) selectPageRef.current.indeterminate = selectedVisible > 0 && !allSelected; }, [allSelected, selectedVisible]);
  const summary = <span className="ledger-panel-summary"><b>{page?.meta.total ?? 0} asientos</b><span className="ledger-panel-summary__currency"><span>Debe {money(page?.totals?.debit ?? 0, rows[0]?.currency ?? "ARS")}</span><span>Haber {money(page?.totals?.credit ?? 0, rows[0]?.currency ?? "ARS")}</span></span></span>;
  return <Panel title="Libro Mayor pendiente" meta={summary} mobileCollapsible>
    <div className="transaction-selection-toolbar"><label className="check-field"><input ref={selectPageRef} type="checkbox" checked={allSelected} disabled={!rows.length || disabled} onChange={() => onTogglePage(rows)} aria-label="Seleccionar esta página de Libro Mayor pendiente"/><span>Seleccionar esta página</span></label><span>{selectedVisible} de {rows.length} en esta página{selected.size > selectedVisible ? ` · ${selected.size} en total` : ""}</span></div>
    <div className="transaction-list">{rows.map((row) => {
      const direction = Number(row.debit ?? 0) !== 0 ? "Debe" : "Haber";
      const title = row.description || row.reference || "Asiento sin descripción";
      return <article className={`transaction-row${selected.has(row.id) ? " transaction-row--selected" : ""}`} key={row.id}>
        <div className="transaction-row__select"><input className="row-check" type="checkbox" checked={selected.has(row.id)} disabled={disabled} onChange={() => onToggle(row)} aria-label={`Seleccionar ${title}`}/></div>
        <div className="transaction-row__main"><strong>{title}</strong><div className="transaction-row__meta"><time className="mono" dateTime={row.transactionDate}>{shortDate(row.transactionDate)}</time>{row.reference && <span>Ref. {row.reference}</span>}</div><Provenance value={row.importBatch}/></div>
        <div className="transaction-row__amount"><span>{direction}</span><b className="mono">{money(rowAmount(row), row.currency)}</b></div>
        <div className="transaction-row__controls"><span className="status status--warning">Pendiente</span>{row.periodRelation === "PRIOR" && <span className="status status--transit">Período anterior</span>}</div>
      </article>;
    })}</div>
    {!rows.length && <Empty>No hay asientos para estos filtros.</Empty>}
    <Pagination meta={page?.meta} onPageChange={onPage}/>
  </Panel>;
}

export function CardWorkspace({ view = "data" }: { view?: CardWorkspaceView }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { hotelId, period } = useWorkspace();
  const canUndoApprovedManual = user?.role === "SUPERVISOR" || user?.role === "ADMIN";
  const [params, setParams] = useSearchParams();
  const masterId = params.get("masterId") ?? "";
  const tab = cardWorkspaceTab(view);
  const autoPage = Number(params.get("reviewAutomaticPage") ?? 1);
  const criteriaPage = Number(params.get("reviewCriteriaPage") ?? 1);
  const page = Number(params.get("page") ?? 1);
  const ledgerPage = Number(params.get("ledgerPage") ?? 1);
  const scope = ["period", "prior", "through"].includes(params.get("scope") ?? "") ? params.get("scope") ?? "through" : "through";
  const q = params.get("q") ?? "";
  const [search, setSearch] = useState(q);
  const [selectedSettlements, setSelectedSettlements] = useState<CardSettlementSelection>(() => new Map());
  const [selectedLedgerEntries, setSelectedLedgerEntries] = useState<CardLedgerSelection>(() => new Map());
  const [selectionSummaryOpen, setSelectionSummaryOpen] = useState(false);
  const workflow = useDomainWorkflow(cardImportConfig);
  const run = useQuery({
    queryKey: ["run", "card", hotelId, period],
    queryFn: async (): Promise<ReconciliationRun | null> => (await responseData(apiClient.GET("/api/v1/cards/reconciliations/latest", { params: { query: { hotelId, period } } }))) ?? null,
    enabled: Boolean(hotelId),
  });
  const autoGroups = useQuery({
    queryKey: ["review-groups", "reconciled", "CARD", hotelId, period, autoPage, masterId],
    queryFn: (): Promise<PageResponse<MatchGroup>> => responseData(apiClient.GET("/api/v1/cards/review/groups", { params: { query: { hotelId, period, page: String(autoPage), pageSize: "20", status: "AUTO,APPROVED,ADJUSTED", cardBrandId: masterId } } })),
    enabled: Boolean(hotelId) && tab === "auto",
  });
  const criteriaGroups = useQuery({
    queryKey: ["review-groups", "criteria", "CARD", hotelId, period, criteriaPage, masterId],
    queryFn: (): Promise<PageResponse<MatchGroup>> => responseData(apiClient.GET("/api/v1/cards/review/groups", { params: { query: { hotelId, period, page: String(criteriaPage), pageSize: "20", status: "SUGGESTED,REVIEW,UNMATCHED", cardBrandId: masterId } } })),
    enabled: Boolean(hotelId) && tab === "criteria",
  });
  const settlements = useQuery({
    queryKey: ["card-settlements", hotelId, period, page, masterId, scope, q, "pending"],
    queryFn: (): Promise<PageResponse<CardSettlement>> => responseData(apiClient.GET("/api/v1/cards/settlements", { params: { query: { hotelId, period, page: String(page), pageSize: "25", cardBrandId: masterId, scope, q: q || undefined } } })),
    enabled: Boolean(hotelId) && tab === "pending",
  });
  const ledger = useQuery({
    queryKey: ["card-ledger", hotelId, period, ledgerPage, masterId, scope, q, "pending"],
    queryFn: (): Promise<PageResponse<MoneyRow>> => responseData(apiClient.GET("/api/v1/cards/ledger-entries", { params: { query: { hotelId, period, page: String(ledgerPage), pageSize: "25", cardBrandId: masterId, scope, q: q || undefined } } })),
    enabled: Boolean(hotelId) && tab === "pending",
  });
  const [selectedCriteriaIds, setSelectedCriteriaIds] = useState<Set<string>>(new Set());
  const selectAllCriteriaRef = useRef<HTMLInputElement>(null);
  const selectableCriteriaIds = criteriaGroups.data?.items.filter((group) => group.status === "SUGGESTED").map((group) => group.id) ?? [];
  const selectableCriteriaKey = selectableCriteriaIds.join(",");
  const allCriteriaSelected = selectableCriteriaIds.length > 0 && selectableCriteriaIds.every((id) => selectedCriteriaIds.has(id));
  const selectedCriteriaCount = selectableCriteriaIds.filter((id) => selectedCriteriaIds.has(id)).length;
  useEffect(() => {
    const visibleIds = new Set(selectableCriteriaIds);
    setSelectedCriteriaIds((current) => {
      const next = new Set([...current].filter((id) => visibleIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [selectableCriteriaKey]);
  useEffect(() => {
    if (selectAllCriteriaRef.current) selectAllCriteriaRef.current.indeterminate = selectedCriteriaCount > 0 && !allCriteriaSelected;
  }, [allCriteriaSelected, selectedCriteriaCount]);
  useEffect(() => { setSearch(q); }, [q]);
  useEffect(() => {
    setSelectedSettlements(new Map());
    setSelectedLedgerEntries(new Map());
    setSelectionSummaryOpen(false);
  }, [hotelId, masterId, period]);
  const setPage = (key: string, value: number) => {
    const search = new URLSearchParams(params);
    search.set(key, String(value));
    setParams(search);
  };
  const applyPendingFilters = (values: Record<string, string>) => {
    const searchParams = new URLSearchParams(params);
    Object.entries(values).forEach(([key, value]) => value ? searchParams.set(key, value) : searchParams.delete(key));
    searchParams.set("page", "1");
    searchParams.set("ledgerPage", "1");
    setParams(searchParams);
  };
  const refresh = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ["review-groups"] }),
    queryClient.invalidateQueries({ queryKey: ["run", "card"] }),
    queryClient.invalidateQueries({ queryKey: ["workflow", "CARD"] }),
    queryClient.invalidateQueries({ queryKey: ["card-settlements"] }),
    queryClient.invalidateQueries({ queryKey: ["card-ledger"] }),
    queryClient.invalidateQueries({ queryKey: ["card-brand-overview"] }),
  ]);
  const approve = useMutation({
    mutationFn: ({ ids, bulk = false }: { ids: string[]; bulk?: boolean }) => {
      const id = ids[0] ?? "";
      return bulk
        ? responseData(apiClient.POST("/api/v1/cards/matches/approve", { body: { ids } })).then(() => undefined)
        : responseData(apiClient.POST("/api/v1/cards/matches/{id}/approve", { params: { path: { id } } })).then(() => undefined);
    },
    onSuccess: async (_result, request) => {
      toast.success(request.bulk ? `${request.ids.length} coincidencias aprobadas.` : "Coincidencia aprobada.");
      setSelectedCriteriaIds((current) => {
        const next = new Set(current);
        request.ids.forEach((id) => next.delete(id));
        return next;
      });
      await refresh();
    },
    onError: (reason) => toast.error(reason, { fallback: "No se pudo aprobar la coincidencia." }),
  });
  const demote = useMutation({
    mutationFn: ({ id }: { id: string; previousStatus: "AUTO" | "APPROVED" }) => responseData(apiClient.POST("/api/v1/cards/matches/{id}/demote", { params: { path: { id } } })),
    onSuccess: async (_result, request) => {
      toast.success(request.previousStatus === "APPROVED" ? "La aprobación se deshizo y la coincidencia volvió a criterio." : "La conciliación automática pasó a criterio.");
      await refresh();
    },
    onError: (reason) => toast.error(reason, { fallback: "No se pudo deshacer la conciliación." }),
  });
  const undoManual = useMutation({
    mutationFn: (id: string) => responseData(apiClient.POST("/api/v1/cards/matches/{id}/unlink", { params: { path: { id } } })),
    onSuccess: async () => {
      toast.success("Conciliación manual deshecha. Los registros volvieron a Pendientes.");
      await refresh();
    },
    onError: (reason) => toast.error(reason, { fallback: "No se pudo deshacer la conciliación manual." }),
  });
  const settlementSelectionTotals = settlementTotals(selectedSettlements);
  const ledgerSelectionTotals = ledgerTotals(selectedLedgerEntries);
  const configuredMaxDifference = workflow.data?.maxApprovableDifference;
  const manualMaxDifference = configuredMaxDifference === undefined ? null : absCents(valueToCents(configuredMaxDifference));
  const manualValidation = validateCardManualSelection(settlementSelectionTotals, ledgerSelectionTotals, manualMaxDifference);
  const manualSelectionCount = selectedSettlements.size + selectedLedgerEntries.size;
  const manualSelectionCountLabel = `${manualSelectionCount} ${manualSelectionCount === 1 ? "seleccionado" : "seleccionados"}`;
  const manualSelectionToggleLabel = manualSelectionCount === 0 ? "Selección · 0"
    : manualValidation.status === "READY" ? `${manualSelectionCountLabel} ·`
      : manualValidation.status === "AMOUNT_MISMATCH" && manualValidation.currency ? `${manualSelectionCountLabel} · Dif. ${centsMoney(manualValidation.difference, manualValidation.currency)}`
        : manualValidation.status === "DIRECTION_MISMATCH" ? `${manualSelectionCountLabel} · Sentido incompatible`
          : manualValidation.status === "CURRENCY_MISMATCH" ? `${manualSelectionCountLabel} · Monedas distintas`
            : manualValidation.status === "CONFIGURATION_MISSING" ? `${manualSelectionCountLabel} · Tope no disponible`
              : selectedSettlements.size === 0 ? `${manualSelectionCountLabel} · Faltan Liquidaciones` : `${manualSelectionCountLabel} · Falta Libro Mayor`;
  const manualSelectionToggleDescription = `Resumen de selección manual: ${selectedSettlements.size} ${selectedSettlements.size === 1 ? "liquidación" : "liquidaciones"} y ${selectedLedgerEntries.size} ${selectedLedgerEntries.size === 1 ? "asiento" : "asientos"} del Libro Mayor. ${manualValidation.message} ${selectionSummaryOpen ? "Contraer resumen." : "Expandir resumen."}`;
  const manual = useMutation({
    mutationFn: () => responseData(apiClient.POST("/api/v1/cards/reconciliations/manual", { body: { hotelId, period, settlementIds: [...selectedSettlements.keys()], ledgerIds: [...selectedLedgerEntries.keys()] } })),
    onSuccess: async (result) => {
      toast.success(result.status === "SUGGESTED" ? "Conciliación manual enviada a revisión." : "Conciliación manual guardada.");
      setSelectedSettlements(new Map());
      setSelectedLedgerEntries(new Map());
      setSelectionSummaryOpen(false);
      await refresh();
    },
    onError: (reason) => toast.error(reason, { fallback: "No se pudo guardar la conciliación manual." }),
  });
  const summary = run.data?.summary ?? {};
  const summaryValue = (key: string) => typeof summary[key] === "number" ? summary[key] as number : 0;
  const error = run.error ?? autoGroups.error ?? criteriaGroups.error ?? settlements.error ?? ledger.error ?? workflow.error;
  return <section className="card-workspace">
    {error && <div className="message message--error">{error instanceof Error ? error.message : "No se pudo cargar la revisión."}</div>}
    {view === "reconciliation" && <section className="operational-summary" aria-label="Resumen de conciliación">
      <div><small>Automáticas</small><strong>{summaryValue("auto")}</strong></div>
      <div><small>Sugeridas</small><strong>{summaryValue("suggested")}</strong></div>
      <div><small>Cupones sin coincidencia</small><strong>{summaryValue("unmatchedOrigin")}</strong></div>
      <div><small>Asientos sin coincidencia</small><strong>{summaryValue("unmatchedCounterpart")}</strong></div>
    </section>}
    {tab === "auto" && <Panel title="Conciliaciones automáticas" meta={`${autoGroups.data?.meta.total ?? 0}`}>
      <div className="match-list match-list--compact">
        {autoGroups.data?.items.length
          ? autoGroups.data.items.map((group) => {
            const manualGroup = group.method === "MANUAL" || (group.ruleCode ?? "").toLowerCase() === "manual";
            const onUndo = group.status === "AUTO"
              ? () => demote.mutate({ id: group.id, previousStatus: "AUTO" })
              : group.status === "APPROVED" && manualGroup && canUndoApprovedManual
                ? () => undoManual.mutate(group.id)
                : group.status === "APPROVED" && !manualGroup
                  ? () => demote.mutate({ id: group.id, previousStatus: "APPROVED" })
                  : undefined;
            return <MatchSpine key={group.id} group={group} variant="compact" onUndo={onUndo}/>;
          })
          : <Empty>{run.data ? "No hay conciliaciones automáticas en esta corrida." : "Cargá Liquidación y Libro Mayor para conciliar automáticamente."}</Empty>}
      </div>
      <Pagination meta={autoGroups.data?.meta} onPageChange={(value) => setPage("reviewAutomaticPage", value)} />
    </Panel>}
    {tab === "criteria" && <Panel title="Coincidencias que requieren criterio" meta={`${criteriaGroups.data?.meta.total ?? 0}`}>
      {selectableCriteriaIds.length > 0 && <div className="criteria-batch-bar">
        <label className="criteria-batch-bar__selector">
          <input ref={selectAllCriteriaRef} type="checkbox" checked={allCriteriaSelected} disabled={approve.isPending} onChange={() => setSelectedCriteriaIds(allCriteriaSelected ? new Set() : new Set(selectableCriteriaIds))} />
          <span>
            <strong>Seleccionar esta página</strong>
            <small>{selectedCriteriaCount ? `${selectedCriteriaCount} de ${selectableCriteriaIds.length} seleccionadas` : `${selectableCriteriaIds.length} disponibles`}</small>
          </span>
        </label>
        <button type="button" className="button button--primary" disabled={!selectedCriteriaCount || approve.isPending} onClick={() => approve.mutate({ ids: selectableCriteriaIds.filter((id) => selectedCriteriaIds.has(id)), bulk: true })}>
          {approve.isPending && approve.variables?.bulk ? "Aprobando selección…" : `Aprobar seleccionadas (${selectedCriteriaCount})`}
        </button>
      </div>}
      <div className="match-list match-list--compact">
        {criteriaGroups.data?.items.length
          ? criteriaGroups.data.items.map((group) => {
            const selectable = group.status === "SUGGESTED";
            const manualGroup = group.method === "MANUAL" || (group.ruleCode ?? "").toLowerCase() === "manual";
            const match = <MatchSpine
              key={group.id}
              group={group}
              variant="compact"
              showDifference
              approveDisabled={approve.isPending}
              approvePending={approve.isPending && Boolean(approve.variables?.ids.includes(group.id))}
              onApprove={selectable ? () => approve.mutate({ ids: [group.id] }) : undefined}
              onUndo={manualGroup && group.status === "SUGGESTED" ? () => undoManual.mutate(group.id) : undefined}
            />;
            return selectable
              ? <div key={group.id} className={`criteria-selectable-match${selectedCriteriaIds.has(group.id) ? " criteria-selectable-match--selected" : ""}`}>
                <div className="criteria-selectable-match__check">
                  <input type="checkbox" checked={selectedCriteriaIds.has(group.id)} disabled={approve.isPending} aria-label={`Seleccionar coincidencia ${group.items[0]?.label ?? group.id}`} onChange={() => setSelectedCriteriaIds((current) => {
                    const next = new Set(current);
                    if (next.has(group.id)) next.delete(group.id);
                    else next.add(group.id);
                    return next;
                  })} />
                </div>
                {match}
              </div>
              : <div key={group.id}>{match}</div>;
          })
          : <Empty>No hay coincidencias pendientes de revisión.</Empty>}
      </div>
      <Pagination meta={criteriaGroups.data?.meta} onPageChange={(value) => setPage("reviewCriteriaPage", value)} />
    </Panel>}
    {tab === "pending" && <form className="manual-link-form card-manual-workspace" onSubmit={(event) => {
      event.preventDefault();
      if (manualValidation.valid && !manual.isPending) manual.mutate();
    }}>
      <div className="card-manual-commandbar">
        <div className="filter-bar card-manual-filters" role="group" aria-label="Filtros de liquidaciones y asientos">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;
              event.preventDefault();
              applyPendingFilters({ q: search });
            }}
            placeholder="Cupón, liquidación, asiento o importe"
            aria-label="Buscar liquidaciones y asientos"
          />
          <select value={scope} onChange={(event) => applyPendingFilters({ scope: event.target.value })} aria-label="Alcance temporal">
            <option value="through">Todos los pendientes al cierre</option>
            <option value="period">Pendientes del período</option>
            <option value="prior">Pendientes de períodos anteriores</option>
          </select>
          <button type="button" className="button button--small" onClick={() => applyPendingFilters({ q: search })}>Buscar</button>
        </div>
        <div className="validation-readiness-actions card-manual-actions">
          <span id="card-manual-reconcile-hint" className="sr-only" aria-live="polite">{manualValidation.message}</span>
          <button
            type="button"
            className={`manual-selection-toggle manual-selection-toggle--${manualValidation.status.toLowerCase()}`}
            aria-controls="card-manual-selection-balance"
            aria-expanded={selectionSummaryOpen}
            aria-label={manualSelectionToggleDescription}
            onClick={() => setSelectionSummaryOpen((current) => !current)}
          >
            <span aria-hidden="true"/>
            <b>{manualSelectionToggleLabel}{manualValidation.status === "READY" && <BalancedCheckIcon className="manual-selection-toggle__check"/>}</b>
            <svg className="manual-selection-toggle__chevron" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m6 9 6 6 6-6"/></svg>
          </button>
          <span className="manual-reconcile-control" role={!manualValidation.valid ? "group" : undefined} aria-label={!manualValidation.valid ? "Conciliación manual" : undefined} aria-describedby={!manualValidation.valid ? "card-manual-reconcile-hint" : undefined} tabIndex={!manualValidation.valid ? 0 : undefined}>
            <button className="button button--primary manual-reconcile-button" disabled={!manualValidation.valid || manual.isPending} aria-describedby={!manualValidation.valid ? "card-manual-reconcile-hint" : undefined}>
              {manual.isPending ? "Conciliando…" : "Conciliar manualmente"}
            </button>
          </span>
        </div>
      </div>
      <div id="card-manual-selection-balance" hidden={!selectionSummaryOpen}>
        <CardManualSelectionBalance
          settlements={selectedSettlements}
          ledgerEntries={selectedLedgerEntries}
          settlementSummary={settlementSelectionTotals}
          ledgerSummary={ledgerSelectionTotals}
          validation={manualValidation}
          disabled={manual.isPending}
          onRemoveSettlement={(row) => setSelectedSettlements((current) => toggleSelectedRow(current, row))}
          onRemoveLedger={(row) => setSelectedLedgerEntries((current) => toggleSelectedRow(current, row))}
          onClearSettlements={() => setSelectedSettlements(new Map())}
          onClearLedger={() => setSelectedLedgerEntries(new Map())}
          onClearAll={() => { setSelectedSettlements(new Map()); setSelectedLedgerEntries(new Map()); setSelectionSummaryOpen(false); }}
        />
      </div>
      <div className="split-ledgers split-ledgers--manual">
        <CardSettlementRows page={settlements.data} selected={selectedSettlements} disabled={manual.isPending} onToggle={(row) => setSelectedSettlements((current) => toggleSelectedRow(current, row))} onTogglePage={(rows) => setSelectedSettlements((current) => toggleSelectedPage(current, rows))} onPage={(value) => setPage("page", value)}/>
        <CardLedgerRows page={ledger.data} selected={selectedLedgerEntries} disabled={manual.isPending} onToggle={(row) => setSelectedLedgerEntries((current) => toggleSelectedRow(current, row))} onTogglePage={(rows) => setSelectedLedgerEntries((current) => toggleSelectedPage(current, rows))} onPage={(value) => setPage("ledgerPage", value)}/>
      </div>
    </form>}
  </section>;
}
